// @flow
import {CommonActions, StackActions} from '@react-navigation/native';
import {cloneDeep, isEqual} from 'lodash';
import {formValueSelector, stopSubmit} from 'redux-form';
import {
  all,
  call,
  fork,
  put,
  race,
  delay,
  select,
  takeEvery,
} from 'redux-saga/effects';
import {tripica} from '../../../api';
import {trackAnalyticsEvent} from '../../../lib/analytics';
import formatCustomer from '../../../lib/format-customer';
import HandleErrorService from '../../../lib/handleError';
import I18n from '../../../lib/i18n';
import Toast from '../../../lib/toastService';
import type {CustomerActionType} from '../../Customer';
import {
  customerSelector,
  hasCustomPaymentMeanSelector,
  hasManuallyEditedIdSelector,
} from '../../Customer/selectors';
import {setError, setLoading} from '../../LoadingStatus/actions';
import {
  initNewOfferWithSelectedSubscription,
  initStateWithOfferSaga,
} from '../../NewOffer/sagas';
import {
  saga as availablePhoneNumbersSaga,
  phoneNumberSelector,
  phoneNumbersSelector,
} from '../AvailablePhoneNumbers';
import {
  saga as bonusSaga,
  godfatherExternalIdForActivateSimSelector,
  isActivationNotFirstSubscriptionSelector,
  promotionSelector,
  setActivationNotFirstSubscription,
  setGodfatherExternalId,
  setGodfatherValidity,
  setPromotion,
  shouldBenefitFromActivationBonusSelector,
} from '../Bonus';
import {handleGodfatherValidityErrors} from '../Bonus/sagas';
import {saga as deliveryAddressSaga} from '../DeliveryAddress';
import {loginSelector, setValidationCode} from '../Login';
import {orderUuidSelector, setOrderUuid} from '../Order';
import {
  choosenPhoneNumberOuidSelector,
  clearChosenPhoneNumber,
  phoneNumberSaga,
} from '../PhoneNumber';
import {internationalFormatPortabilityCharacteristicsSelector} from '../PhoneNumber/reducer';
import type {SignUpActionType} from '../actions';

import {
  currentSubscriptionSelector,
  hasRefreshedAtLeastOnceSelector,
  selectSubscription,
} from '../../Dashboard';
import {default as paymentSaga} from '../../Payment/sagas';
import {
  getSubscriptionToEditOuidSelector,
  subscriptionsSelector,
} from '../../Subscriptions';
import {setCurrentSubscription} from '../../Subscription/actions';
import {identificationDocumentSelector} from '../Identifications/reducer';
import {SIMSerialNumberSelector} from '../SIMCardScan';
import {default as SIMCardSaga} from '../SIMCardScan/sagas';
import {
  eSimSelectedSelector,
  simResourceSelector,
} from '../../../modules/ESim';

import {
  goToNextActivationPage,
  goToNextActivationPageSaga,
} from './goToNextActivationPage';
import {goToNextOrderPage} from './goToNextOrderPage';
import {triggerEmailValidation} from './triggerEmailValidation';

import {default as simProductSaga} from '../Sim/sagas';
import {orderSimAPICall} from './orderSimAPICall';
import {checkLogin, pollForActivation} from './polling';

import {selectedAgreementSpecificationsSelector} from '../../Agreements/selectors';

import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import config from '../../../config';
import Navigation from '../../../lib/navigation';
import {getTrigramFromPostCode} from '../../../scenes/SignUp/Address/lib/getInfosFromPostCode';
import {fetchCustomerSaga} from '../../Customer/sagas';
import {resetToDashboard} from '../../Navigation';
import {resetOfferState} from '../../NewOffer/actions';
import {newOfferOptionsSelector} from '../../NewOffer/selectors';
import {paymentMeanSelector} from '../../Payment/selector';
import {
  triggerPaymentProductOrderPolling,
  triggerProductOrderPolling,
} from '../../PollingOrder/saga';
import {voucherCurrentVoucherDetailsSelector} from '../../Voucher';
import {bonusErrorsAndSpecificTranslations} from '../Bonus/constants';
import {saga as checkOrderConsistencySaga} from '../CheckOrderConsistency';
import {updateDeliveryContatMediumSaga} from '../DeliveryContact/sagas';
import {setRegion} from '../Region';
import {initializeSubscription} from './initializeSubscription';
import {register} from './register';
import {goToNextEsimPage} from './goToNextEsimPage';
import {eSimSelectedSuccess} from '../../ESim/actions';
import {freeTrialActivationBonusStateSelector} from '../../Bonuses';
import { loadTaxExemptionByArea } from '../../../lib/tripicaStaticConfig';
import WizzeeLog from '../../../lib/wizzeeLog';

export {goToNextActivationPage, orderSimAPICall};

export function* redirectAfterOrderingSIM(
  action: SignUpActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_REDIRECT_AFTER_ORDERING_SIM') return; // for flow
  const uuid = yield select(orderUuidSelector);

  yield put(setLoading('redirectAfterOrderingSIM', true));
  yield put(setError('redirectAfterOrderingSIM', null));

  const hasRefreshedAtLeastOnce = yield select(hasRefreshedAtLeastOnceSelector);

  // $FlowFixMe
  const {timeout} = yield race({
    orderStatusCheck: call(checkLogin),
    timeout: delay(35000),
  });

  let productOrder;
  if (!timeout) {
    productOrder = yield call(
      triggerPaymentProductOrderPolling,
      'ORDER_SIM',
      uuid,
    );
  }

  switch (productOrder?.state) {
    case 'COMPLETED':
      trackAnalyticsEvent('sim_ordered_success');
      break;
    case 'FAILED':
    case undefined:
    case null:
      yield put(
        setError('redirectAfterOrderingSIM', {
          type: 'FAILED',
          title: I18n.t('error.orderFailed'),
          icon: 'failurePhoneImage',
        }),
      );
      break;
    case 'IN_PROGRESS':
    default:
      yield put(
        setError('redirectAfterOrderingSIM', {
          type: 'IN_PROGRESS',
          title: I18n.t('SignUp.Congrats.orderInProgress'),
          icon: 'orderFailedImage',
        }),
      );
      break;
  }
  if (hasRefreshedAtLeastOnce) {
    yield call(resetToDashboard);
  } else {
    yield call(
      Navigation.dispatch,
      CommonActions.reset({
        index: 0,
        routes: [{name: 'wellDone', params: {cannotGoBack: true}}],
      }),
    );
  }
  yield put(setLoading('redirectAfterOrderingSIM', false));
}

export function* resendEmail(action: SignUpActionType): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_RESEND_EMAIL') return; // for flow

  try {
    yield put(setOrderUuid(null)); // reset sandbox user
    const response = yield call(register);
    if (!response || !!response.errorCodeList)
      yield put(setOrderUuid(response.uuid));
  } catch (e) {
    WizzeeLog.warn('[saga] resendEmail', e);
    HandleErrorService.showToastError(e);
  }
}

export function* validateEmail(action: SignUpActionType): Generator<*, *, *> {
  if (
    action.type !== 'SIGNUP_VALIDATE_EMAIL' &&
    action.type !== 'SIGNUP_VALIDATE_EMAIL_DURING_ACTIVATE'
  )
    return; // for flow
  yield put(setLoading('validateEmail', true));
  yield put(setValidationCode(action.payload.code));
  try {
    const response = yield call(register);
    if (!response || !response.errorCodeList) {
      throw new Error('no response or error code list');
    }
    if (response.errorCodeList.includes('BAD_ORDER_VALIDATION_CODE')) {
      yield put(
        stopSubmit('validateEmail', {
          _error: I18n.t('SignUp.ValidateEmail.invalidCode'),
        }),
      );
      yield put(setValidationCode(null));
    } else {
      trackAnalyticsEvent('registration_success', {
        customerOuid: response.customerOuid,
      });
      yield call(fetchCustomerSaga);
      if (action.type.includes('DURING_ACTIVATE')) {
        yield call(goToNextActivationPage, true);
      } else {
        if (config.askPersonalInformationDuringOrderSim) {
          yield call(
            Navigation.dispatch,
            StackActions.replace('identityRequirements'),
          );
        }
      }
    }
  } catch (e) {
    WizzeeLog.warn('[saga] validateEmail', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('validateEmail', false));
  }
}

export function* confirmSignUp(action: SignUpActionType): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_CONFIRM_PLAN') return; // for flow
  yield put(setLoading('confirmPlan', true));

  let error = false;
  try {
    const specificErrors = Object.keys(bonusErrorsAndSpecificTranslations);

    const isOrderingReplacementSim =
      action.payload && action.payload.isOrderingReplacementSim;

    if (isOrderingReplacementSim) {
      const response = yield call(orderSimAPICall);
      if (!response || !response.errorCodeList) {
        throw new Error('no response or error code list');
      }

      const currentSpecificError = specificErrors.find(specificError =>
        response.errorCodeList.includes(specificError),
      );
      const currentSpecificErrorTranslation =
        currentSpecificError &&
        bonusErrorsAndSpecificTranslations[currentSpecificError];
      if (response.status === 'VALIDATED') {
        yield put(setGodfatherExternalId(null));
        yield put(setPromotion(null));
        yield call(
          Navigation.dispatch,
          CommonActions.reset({
            index: 0,
            routes: [{name: 'swapOrderSuccess', params: {cannotGoBack: true}}],
          }),
        );
      } else if (currentSpecificError && currentSpecificErrorTranslation) {
        if (currentSpecificError.includes('GODFATHER')) {
          yield put(setGodfatherValidity(false));
        }
        Toast.showError(I18n.t(currentSpecificErrorTranslation));

        if (currentSpecificError.includes('DELIVERY_PARTNER_CLOSED')) {
          error = true;
        }
      } else {
        throw new Error(response.errorCodeList.join(', '));
      }
    } else {
      const response = yield call(initializeSubscription);
      if (response) {
        yield put(setOrderUuid(response.uuid));
        const orderSim = yield call(orderSimAPICall);

        if (!orderSim || !orderSim.errorCodeList) {
          throw new Error('no response or error code list');
        }
        yield call(redirectAfterOrderingSIM, {
          type: 'SIGNUP_REDIRECT_AFTER_ORDERING_SIM',
        });
      } else {
        yield call(resetToDashboard);
      }
    }
  } catch (e) {
    WizzeeLog.warn('[saga] confirmPlan', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('confirmPlan', false));
  }

  return {error};
}

export function* initializeSignUp(
  action: SignUpActionType,
  routeName?: string = 'chooseRegion',
  routeParams: Object = {},
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_INIT') return; // for flow
  yield put(setLoading('initSignUp', true));

  try {
    const response = yield call(initStateWithOfferSaga);
    if (response && response.error) {
      throw response.error;
    }

    yield call(
      Navigation.dispatch,
      CommonActions.navigate({name: routeName, params: routeParams}),
    );
  } catch (e) {
    WizzeeLog.warn('[saga] initializeSignUp', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('initSignUp', false));
  }
}

export function* initSimReplacementSaga(
  action: SignUpActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_INIT_SIM_REPLACEMENT') return; // for flow
  // Forcing eSimSelected to be false
  yield put(eSimSelectedSuccess(false));
  yield put(clearChosenPhoneNumber());
  yield call(initializeSignUp, {type: 'SIGNUP_INIT'}, 'deliveryAddress', {
    isOrderingReplacementSim: true,
  });
}

export function* initEsimReplacementSaga(
  action: SignUpActionType,
): Generator<*, *, *> {
  yield put(setLoading('swapActivateSuccess', true));
  if (action.type !== 'SIGNUP_INIT_ESIM_REPLACEMENT') return; // for flow

  if (action.payload.subscription) {
    yield put(setCurrentSubscription(action.payload.subscription));
  }

  yield put(clearChosenPhoneNumber());
  yield call(initializeSignUp, {type: 'SIGNUP_INIT'}, 'confirmPlan', {
    isOrderingReplacementSim: true,
  });
  yield put(setLoading('swapActivateSuccess', false));
}

export function* confirmPersonalInfo(
  action: CustomerActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_CONFIRM_PERSONAL_INFO') return; // for flow
  yield put(setLoading('confirmPersonalInfo', true));
  yield call(fetchCustomerSaga);
  let customer = formatCustomer(action.payload);
  const storedCustomer = yield select(customerSelector);

  const sameAddresses = isEqual(customer.billingAddress, customer.idAddress);
  if (action.meta.useAddressAsBilling) {
    customer.billingAddress = customer.idAddress;
  } else if (sameAddresses) {
    customer.billingAddress = {};
  }

  try {
    const formSelector = formValueSelector('personalInfo');
    const identificationId = yield select(
      formSelector,
      'identification.identificationId',
    );
    const identification = yield select(identificationDocumentSelector);
    const {familyName, givenName, birthdate, nationality, gender, middleName} =
      customer;
    const contactPhoneNumber = yield select(formSelector, 'contactPhoneNumber');

    const trpcFile = yield call(
      [tripica.formatter, 'buildTrpcFileFromIndividualIdentification'],
      customer.ouid,
      customer.identification,
    );

    const note = yield call(
      [tripica.formatter, 'buildNoteFromTrpcFile'],
      trpcFile,
    );

    const updatedIdentification = identification && {
      ...identification,
      identificationId,
      ouid: storedCustomer.identification?.ouid,
      contentName: note.file.fileName ? note.file.fileName : '',
      characteristics: {
        ...(identification.characteristics || {}),
        // expirationDateTime, //no Need?
      },
    };

    const fullName = `${givenName} ${middleName ?? ''} ${familyName}`
      .replace(/\s+/g, ' ')
      .trim();

    const updatedIndividual = customer.individual && {
      ...customer.individual,
      familyName,
      givenName,
      birthdate,
      nationality: !!nationality ? nationality : 'FRA',
      gender,
      fullName,
      middleName,
      identifications: updatedIdentification ? [updatedIdentification] : [],
      status: 'VALIDATED',
    };

    const response = yield call(
      [tripica, 'updateParty'],
      customer.ouid,
      updatedIndividual,
      'INDIVIDUAL',
      undefined,
      undefined,
      undefined,
      [note],
    );

    WizzeeLog.log('>>> confirmPersonalInfo - customer.ouid', customer.ouid);
    WizzeeLog.log('>>> confirmPersonalInfo - updatedIndividual', updatedIndividual);
    WizzeeLog.log('>>> confirmPersonalInfo - note', [note]);
    WizzeeLog.log('>>> confirmPersonalInfo - updateParty response', response);

    if (response && response.status === 'VALIDATED') {
      WizzeeLog.log('>>> confirmPersonalInfo - updateParty validated');
      const productOrder = yield call(
        triggerProductOrderPolling,
        response.uuid,
      );
      if (productOrder.state === 'COMPLETED') {
        WizzeeLog.log('>>> confirmPersonalInfo - productOrder.state COMPLETED');
        yield call(fetchCustomerSaga);
        WizzeeLog.log('>>> confirmPersonalInfo - fetchCustomerSaga OK');
        yield call(updateDeliveryContatMediumSaga, {
          payload: {
            deliveryContact: {name: fullName, phoneNumber: contactPhoneNumber},
          },
          type: 'SIGNUP_SET_DELIVERY_CONTACT',
        });

        if (action.meta.isActivatingSIM) {
          yield call(goToNextActivationPage);
        } else {
          let nextPage = '';
          if (
            config.askPersonalInformationDuringOrderSim ||
            !action.meta.useAddressAsBilling
          ) {
            nextPage = 'billingAddress';
          } else {
            nextPage = 'selfieCheck';
          }
          yield call(
            Navigation.dispatch,
            CommonActions.navigate({name: nextPage}),
          );
        }
      } else {
        throw new Error('validateIndividual product order failed');
      }
    } else {
      throw new Error('status not validated');
    }
  } catch (e) {
    WizzeeLog.warn('[saga] confirmPersonalInfo', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('confirmPersonalInfo', false));
  }
}

export function* activateSimCall(): Generator<*, *, *> {
  const subscription = yield select(currentSubscriptionSelector);
  let phoneNumberOuid;

  const eSimSelected = yield select(eSimSelectedSelector);
  WizzeeLog.log('>>> activateSimCall() - eSimSelected ? ', eSimSelected);

  const portabilityCharacteristics = yield select(
    internationalFormatPortabilityCharacteristicsSelector,
  );

  if (subscription && !eSimSelected) {
    phoneNumberOuid = subscription.line.resourceOuid;
  } else if (portabilityCharacteristics && config.useTemporaryMsisdn) {
    const phonenumbers = yield select(phoneNumbersSelector);
    phoneNumberOuid = phonenumbers[0].ouid;
  } else {
    phoneNumberOuid = yield select(choosenPhoneNumberOuidSelector);
  }

  let customer = cloneDeep(yield select(customerSelector));
  const customerHasPaymentMean = yield select(hasCustomPaymentMeanSelector);
  const paymentMean = !customerHasPaymentMean
    ? yield select(paymentMeanSelector)
    : null;
  let paymentMeanOuid;
  if (paymentMean) {
    paymentMeanOuid = paymentMean.ouid;
  }
  const formatedProductOfferings = [];
  const catalog = yield call([tripica, 'getCatalog']);
  if (catalog.allOptions) {
    const options = yield select(newOfferOptionsSelector);
    const orderableOptions = options.filter(
      opt => opt.ouid && opt.characteristic?.value !== '0',
    );
    orderableOptions.forEach(option => {
      const formatedProductOffering = TrpcLibrary.getCatalogByProductName(
        option.name,
        catalog.allOptions[option.type],
      );
      formatedProductOfferings.push(formatedProductOffering);
    });
  }

  // Get taxExemptionByArea from async storage
  const taxExemptionByArea = yield call(loadTaxExemptionByArea);
  if (taxExemptionByArea) {
    WizzeeLog.log('>>> taxExemptionByArea to apply on customer update', taxExemptionByArea);
  }

  const simReference = yield select(SIMSerialNumberSelector);
  WizzeeLog.log('>>> activateSimCall() - simReference ? ', simReference);

  // Build simResource object for the new SDK signature
  const simResource = eSimSelected
    ? yield select(simResourceSelector)
    : {type: 'STANDARD_SIM', resourceSerialNumber: simReference};
  WizzeeLog.log('>>> activateSimCall() - simResource ? ', JSON.stringify(simResource));

  customer.hasEditedIdentificationInfo = yield select(
    hasManuallyEditedIdSelector,
  );

  const {email, password, validationCode} = yield select(loginSelector);
  if (!customer.ouid) {
    customer.login = {email, password, validationCode};
  }

  if (customer.billingAddress) {
    customer.billingAddress.stateOrProvince = getTrigramFromPostCode(
      customer.billingAddress.postCode,
    );
  }
  if (customer.nationality === null || customer.nationality === '') {
    customer.nationality = 'UNKNOWN';
  }

  const promotion = yield select(promotionSelector);
  if (promotion) {
    formatedProductOfferings.push(promotion.productOffering);
  }

  const godfatherExternalId = yield select(
    godfatherExternalIdForActivateSimSelector,
  );
  let activationBonus = yield select(shouldBenefitFromActivationBonusSelector);

  // Checking if free trial bonus is available
  let freeTrialEnabled = false;
  const freeTrialActivationBonus = yield select(
    freeTrialActivationBonusStateSelector,
  );
  // Checking if FREE_TRIAL product is available in the catalog
  if (freeTrialActivationBonus && !!freeTrialActivationBonus.freeTrial) {

    // Then check if customer is eligible for free trial
    const response = yield call(
      [tripica, 'isActivationBonusEligible'],
      catalog,
    );
    if (response) {
      freeTrialEnabled = true;
    }
  }
  WizzeeLog.log('>>> activateSimCall() - freeTrialEnabled :: ', freeTrialEnabled);

  const isNotFirstSubscription = yield select(
    isActivationNotFirstSubscriptionSelector,
  );
  if (isNotFirstSubscription) {
    activationBonus = false;
  }
  const bonuses = {
    godfatherExternalId: godfatherExternalId,
    activationBonus: freeTrialEnabled,
  };

  let phoneNumber = yield select(phoneNumberSelector, phoneNumberOuid);
  if (phoneNumber === undefined) phoneNumber = subscription.line.number;

  const agreementSpecifications = yield select(
    selectedAgreementSpecificationsSelector,
  );

  // Handling voucher if any selected
  const voucherDetails = yield select(voucherCurrentVoucherDetailsSelector);

  const response = yield call(
    [tripica, 'activateLine'],
    customer,
    simResource,
    eSimSelected ? null : subscription,
    catalog,
    bonuses,
    formatedProductOfferings,
    voucherDetails,
    false,
    subscription?.billingAccountOuid || null,
    paymentMeanOuid,
    phoneNumberOuid,
    phoneNumber.phoneNumber,
    agreementSpecifications,
    portabilityCharacteristics,
    undefined,
    taxExemptionByArea ?? undefined
  );

  return response;
}

export function* activateSim(action: SignUpActionType): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_ACTIVATE_SIM') return; // for flow
  yield put(setLoading('activateSim', true));

  let error = false;
  let errorCodeList = [];
  try {
    const result = yield* activateSimCall();
    if (!result) {
      throw new Error();
    }

    if (result.errorCodeList && result.errorCodeList.length) {
      errorCodeList = result.errorCodeList;
      trackAnalyticsEvent('activation_failed', {error: errorCodeList[0]});
      if (result.errorCodeList.includes('MSISDN_NOT_AVAILABLE')) {
        Toast.showError(I18n.t('SignUp.ChooseNumber.numberNotAvailable'));
        error = true;
      } else if (result.errorCodeList.includes('TOO_SOON')) {
        Toast.showError(I18n.t('SignUp.ConfirmPlan.tooSoon'));
        error = true;
      } else {
        yield call(handleGodfatherValidityErrors, result.errorCodeList);
        if (result.errorCodeList.includes('NOT_FIRST_SUBSCRIPTION')) {
          Toast.showError(
            I18n.t(
              'SignUp.ConfirmPlan.referralPromotion.invalid.notFirstSubscription',
            ),
          );
          error = true;
          yield put(setActivationNotFirstSubscription(true));
        } else {
          throw new Error(result.errorCodeList.join(', '));
        }
      }
    } else {
      yield put(setGodfatherExternalId(null));
      yield put(setPromotion(null));
      yield put(setOrderUuid(result.uuid));
      yield fork(pollForActivation);
      let trackingParams;
      if (
        result.signup &&
        result.signup.login &&
        result.signup.login.externalId
      ) {
        trackingParams = {externalId: result.signup.login.externalId};
      }
      trackAnalyticsEvent('activation_validated', trackingParams);
      yield call(
        Navigation.dispatch,
        CommonActions.reset({
          index: 0,
          routes: [{name: 'congrats'}],
        }),
      );
    }
  } catch (e) {
    WizzeeLog.warn('[saga] activateSimSaga', e);
    HandleErrorService.showToastError(e);
    error = true;
  } finally {
    yield put(setLoading('activateSim', false));
  }

  return {error, errorCodeList};
}

export function* selectSubscriptionForActivation(): Generator<*, *, *> {
  const subscriptions = yield select(subscriptionsSelector);

  if (!subscriptions.length) return yield put(selectSubscription(null));

  const hasSubscriptionsWithOptions = subscriptions.some(
    subscription =>
      !!subscription.options.length || subscription.status !== 'CREATED',
  );
  if (!hasSubscriptionsWithOptions) {
    return yield put(selectSubscription(subscriptions[0]));
  }
  yield put(selectSubscription(null));
}

export function* confirmSubscriptionSelectionDuringActivateSaga(
  action: SignUpActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_CONFIRM_SUBSCRIPTION_SELECTION_DURING_ACTIVATE')
    return;
  yield put(selectSubscription(action.payload.subscription));
  yield put(setLoading('confirmSubscriptionSelectionDuringActivate', true));
  yield put(setRegion(null));
  const lineToUpdateOuid = yield select(getSubscriptionToEditOuidSelector);
  yield call(initNewOfferWithSelectedSubscription);
  if (!!lineToUpdateOuid) {
    yield put(resetOfferState());
    yield call(
      Navigation.dispatch,
      CommonActions.navigate('connected', {
        screen: 'stack',
        params: {screen: 'editPlanWithUnavailableOfferDuringActivate'},
      }),
    );
  } else {
    yield call(goToNextActivationPage);
  }
  yield put(setLoading('confirmSubscriptionSelectionDuringActivate', false));
}

export function* confirmRegionDuringActivateSaga(
  action: SignUpActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_CONFIRM_REGION_DURING_ACTIVATE') return;
  yield put(setLoading('confirmRegionDuringActivate', true));
  yield call(initStateWithOfferSaga, resetOfferState());
  yield call(goToNextActivationPage);
  yield put(setLoading('confirmRegionDuringActivate', false));
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('SIGNUP_TRIGGER_EMAIL_VALIDATION', triggerEmailValidation),
    takeEvery(
      'SIGNUP_TRIGGER_EMAIL_VALIDATION_DURING_ACTIVATE',
      triggerEmailValidation,
    ),
    takeEvery('SIGNUP_RESEND_EMAIL', resendEmail),
    takeEvery('SIGNUP_VALIDATE_EMAIL', validateEmail),
    takeEvery('SIGNUP_VALIDATE_EMAIL_DURING_ACTIVATE', validateEmail),
    takeEvery('SIGNUP_CONFIRM_PLAN', confirmSignUp),
    takeEvery('SIGNUP_REDIRECT_AFTER_ORDERING_SIM', redirectAfterOrderingSIM),
    takeEvery('SIGNUP_CONFIRM_PERSONAL_INFO', confirmPersonalInfo),
    takeEvery('SIGNUP_INIT', initializeSignUp),
    takeEvery('SIGNUP_INIT_SIM_REPLACEMENT', initSimReplacementSaga),
    takeEvery('SIGNUP_INIT_ESIM_REPLACEMENT', initEsimReplacementSaga),
    takeEvery('SIGNUP_ACTIVATE_SIM', activateSim),
    takeEvery('SIGNUP_GO_TO_NEXT_ACTIVATION_PAGE', goToNextActivationPageSaga),
    takeEvery('SIGNUP_GO_TO_NEXT_ORDER_PAGE', goToNextOrderPage),
    takeEvery('SIGNUP_GO_TO_NEXT_ESIM_PAGE', goToNextEsimPage),
    takeEvery(
      'SIGNUP_CONFIRM_SUBSCRIPTION_SELECTION_DURING_ACTIVATE',
      confirmSubscriptionSelectionDuringActivateSaga,
    ),
    takeEvery(
      'SIGNUP_CONFIRM_REGION_DURING_ACTIVATE',
      confirmRegionDuringActivateSaga,
    ),
    fork(SIMCardSaga),
    fork(simProductSaga),
    fork(paymentSaga),
    fork(phoneNumberSaga),
    fork(deliveryAddressSaga),
    fork(availablePhoneNumbersSaga),
    fork(bonusSaga),
    fork(checkOrderConsistencySaga),
  ]);
}
